<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { IonContent, IonButton, IonIcon, IonSpinner } from '@ionic/vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { chevronBack, alertCircle } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import useQueue from '@/modules/queue/composables/useQueue';
import useToast from '@/shared/composables/useToast';
import { anykrowdApi } from '@/api/anykrowdApi';

// Minimal setup
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { presentToast } = useToast();

const activityId = ref(parseInt(route.params.activityId as string));

// Navigation helpers
const navigateToQueues = () => {
	router.replace({ name: 'queue-activities' });
};

// Queue composables
const {
	getQueueActivity,
	formatPrice,
} = useQueue();

// Load activity data
const { activity, isFetchedActivity } = getQueueActivity(activityId.value);

// Loading state
const isLoading = computed(() => !isFetchedActivity.value);
const isCheckingAvailability = ref(false);

// Swiper modules
const swiperModules = [Pagination];

// Computed property for all images (main image + gallery)
const allImages = computed(() => {
	if (!activity.value) return [];
	const images = [];

	// Add main image first
	if (activity.value.image_url) {
		images.push(activity.value.image_url);
	}

	// TODO: Uncomment to enable gallery images
	// Add gallery images
	// if (activity.value.gallery_images && activity.value.gallery_images.length > 0) {
	// 	images.push(...activity.value.gallery_images);
	// }

	return images;
});

// Check if activity is available for booking
const checkAvailabilityAndNavigate = async () => {
	if (!activity.value) {
		await presentToast('top', t('queue.no_activities_available'), 3000, 'danger');
		return;
	}

	isCheckingAvailability.value = true;

	try {
		// Check the activity's availability
		const { data } = await anykrowdApi.get(
			`/queue/activities/${activity.value.id}/can-book?queue_type=normal`,
			true
		);

		if (!data.can_book) {
			await presentToast('top', data.reason || t('queue.not_available_for_booking'), 5000, 'danger');
			return;
		}

		// Check queue type and navigate accordingly
		if (activity.value.queue_type === 'chronological') {
			// For chronological queues, skip date selection and go directly to ticket purchase
			router.push({
				name: 'queue-tickets-buy',
				params: {
					activityId: activity.value.id
				}
			});
		} else {
			// For time slot queues, navigate to date selection page
			router.push({
				name: 'queue-date-selection',
				params: {
					activityId: activity.value.id
				}
			});
		}
	} catch (error: any) {
		console.error('Error checking availability:', error);
		await presentToast('top', t('queue.error_checking_availability'), 3000, 'danger');
	} finally {
		isCheckingAvailability.value = false;
	}
};
</script>

<template>
	<ion-content class="queue-activity-detail">
		<!-- Loading state -->
		<div v-if="isLoading" class="flex justify-center items-center min-h-screen">
			<IonSpinner name="dots" class="text-white text-4xl" />
		</div>

		<!-- Activity not found -->
		<div v-else-if="!activity" class="flex flex-col items-center justify-center min-h-screen px-6 text-center">
			<IonIcon :icon="alertCircle" class="text-6xl text-white/60 mb-4" />
			<h2 class="text-2xl font-bold text-white mb-2">{{ t('queue.activity_not_found') }}</h2>
			<p class="text-white/70 mb-6">{{ t('queue.activity_not_found_description') }}</p>
			<IonButton @click="router.back()" fill="outline" color="light">
				{{ t('general.go_back') }}
			</IonButton>
		</div>

		<!-- Activity content -->
		<div v-else class="pb-32">
			<!-- Header with back button -->
			<div class="header-section">
				<div class="flex items-center px-6 pt-16 pb-6">
					<IonButton
						fill="clear"
						color="light"
						@click="navigateToQueues"
						class="back-button">
						<IonIcon :icon="chevronBack" class="back-icon" />
					</IonButton>
					<h1 class="header-title">{{ t('queue.activity_details') }}</h1>
				</div>
			</div>

			<!-- Hero Image / Gallery -->
			<div class="hero-image-container">
				<Swiper
					v-if="allImages.length > 0"
					:modules="swiperModules"
					:pagination="{ clickable: true }"
					:slides-per-view="1"
					class="image-swiper">
					<SwiperSlide v-for="(image, index) in allImages" :key="index">
						<img
							:src="image"
							:alt="`${activity.name} - Image ${index + 1}`"
							class="w-full h-full object-cover rounded-2xl hero-image" />
					</SwiperSlide>
				</Swiper>
				<img
					v-else
					src="/api/placeholder/400/320"
					:alt="activity.name"
					class="w-full h-full object-cover rounded-2xl hero-image" />
			</div>

			<!-- Content -->
			<div class="content-section">
				<!-- Title and Price Row -->
				<div class="title-price-row">
					<h2 class="activity-title">{{ activity.name }}</h2>
					<div v-if="activity.regular_price || activity.priority_price" class="activity-price">
						{{ formatPrice(activity.regular_price || activity.priority_price, activity.regularCurrency || activity.priorityCurrency) }}
					</div>
				</div>

				<!-- About Section -->
				<div class="about-section">
					<h3 class="section-title">{{ t('queue.about_this_activity') }}</h3>
					<div v-if="activity.description" class="description-text" v-html="activity.description"></div>
				</div>
			</div>

			<!-- Bottom Bar -->
			<div class="bottom-bar">
				<div class="bottom-bar-backdrop"></div>
				<div class="bottom-bar-content">
					<IonButton
						expand="block"
						fill="solid"
						class="book-now-button"
						@click="checkAvailabilityAndNavigate"
						:disabled="activity.status !== 'published' || isCheckingAvailability">
						<ion-spinner v-if="isCheckingAvailability" name="dots"></ion-spinner>
						<span v-else>{{ t('queue.book_now') }}</span>
					</IonButton>
				</div>
			</div>
		</div>
	</ion-content>
</template>

<style lang="scss" scoped>
.queue-activity-detail {
	--background: #17161e;
}

ion-content {
	--background: #17161e;
}

// Header Section
.header-section {
	background: #17161e;

	.back-button {
		--color: #fff;
		--padding-start: 0;
		--padding-end: 0;
		width: 40px;
		height: 40px;
		margin-right: 16px;
	}

	.back-icon {
		font-size: 24px;
		color: #fff;
	}

	.header-title {
		font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, sans-serif;
		font-size: 20px;
		font-weight: 600;
		color: #fff;
		margin: 0;
	}
}

// Hero Image Container
.hero-image-container {
	margin: 24px;
	height: 280px;

	.image-swiper {
		height: 100%;
		border-radius: 16px;

		:deep(.swiper-pagination) {
			bottom: 10px;
		}

		:deep(.swiper-pagination-bullet) {
			background: rgba(255, 255, 255, 0.5);
			opacity: 1;
		}

		:deep(.swiper-pagination-bullet-active) {
			background: #ffffff;
		}
	}

	.hero-image {
		border-radius: 16px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
	}
}

// Content Section
.content-section {
	padding: 0 34px;
}

// Title and Price Row
.title-price-row {
	margin-bottom: 32px;

	.activity-title {
		font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, sans-serif;
		font-size: 26px;
		font-weight: 600;
		line-height: 32px;
		color: #ffffff;
		margin: 0;
	}

	.activity-price {
		font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, sans-serif;
		font-size: 26px;
		font-weight: 700;
		line-height: 22px;
		color: #ffffff;
		margin-top: 8px;
	}
}

// About Section
.about-section {
	margin-bottom: 32px;

	.section-title {
		font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, sans-serif;
		font-size: 20px;
		font-weight: 600;
		line-height: 32px;
		color: #ffffff;
		margin: 0 0 16px 0;
	}

	.description-text {
		font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, sans-serif;
		font-size: 16px;
		line-height: 26px;
		color: rgba(255, 255, 255, 0.5);
		margin: 0;

		:deep(p) {
			margin-bottom: 16px;
			line-height: 1.6;

			&:last-child {
				margin-bottom: 0;
			}
		}

		:deep(strong), :deep(b) {
			font-weight: 600;
		}

		:deep(em), :deep(i) {
			font-style: italic;
		}
	}
}

// Bottom Bar
.bottom-bar {
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 1000;
	height: 96px;

	.bottom-bar-backdrop {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 96px;
		background: rgba(23, 22, 30, 0.8);
		backdrop-filter: blur(20px);
		-webkit-backdrop-filter: blur(20px);
	}

	.bottom-bar-content {
		position: relative;
		padding: 14px 30px 30px 30px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	ion-button.book-now-button {
		--background: #ffffff;
		--color: #18171e;
		--border-radius: 52px;
		--padding-top: 14px;
		--padding-bottom: 14px;
		--padding-start: 30px;
		--padding-end: 30px;
		font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, sans-serif;
		font-size: 18px;
		font-weight: 600;
		line-height: 0;
		text-align: center;
		min-height: 52px;
		width: 100%;
		max-width: 325px;

		&:hover {
			--background: #ffffff !important;
			--color: #18171e;
		}

		&:active,
		&.ion-activated {
			--background: #f5f5f5;
			--color: #18171e;
		}

		&:disabled {
			--background: #666666;
			--color: #999999;
		}
	}
}

// Dark theme overrides
ion-spinner {
	--color: #fff;
}

ion-button {
	&[color="light"] {
		--color: #fff;
	}
}

// Font loading for Source Sans Pro if available
@import url('https://fonts.googleapis.com/css2?family=Source+Sans+Pro:ital,wght@0,400;0,600;0,700;1,400&display=swap');
</style>
